import { useEffect, useState, type FormEvent } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import {
  EntryCategoryLabels, type Entry, type EntryCategory,
} from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';

const CATEGORIES: EntryCategory[] = ['note', 'med', 'meal', 'appointment', 'symptom', 'handover'];

interface Props {
  entry: Entry | null;
  open: boolean;
  onClose: () => void;
}

export default function EntryEditDialog({ entry, open, onClose }: Props) {
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(null);
  const [category, setCategory] = useState<EntryCategory>('note');
  const [body, setBody] = useState('');
  const [medName, setMedName] = useState('');
  const [medDose, setMedDose] = useState('');
  const qc = useQueryClient();

  useEffect(() => {
    if (entry) {
      setOccurredAt(dayjs(entry.occurredAt));
      setCategory(entry.category);
      setBody(entry.body);
      setMedName(entry.medName ?? '');
      setMedDose(entry.medDose ?? '');
    }
  }, [entry]);

  const mutation = useMutation({
    mutationFn: () => api.patch<Entry>(`/api/entries/${entry!.id}`, {
      occurredAt: occurredAt?.toISOString(),
      category,
      body: body.trim(),
      medName: category === 'med' ? (medName.trim() || null) : null,
      medDose: category === 'med' ? (medDose.trim() || null) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries'] });
      onClose();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() || !occurredAt) return;
    mutation.mutate();
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit entry</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <DateTimePicker
              label="When"
              value={occurredAt}
              onChange={setOccurredAt}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TextField
              select
              size="small"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as EntryCategory)}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>{EntryCategoryLabels[c]}</MenuItem>
              ))}
            </TextField>
            {category === 'med' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Medication"
                  value={medName}
                  onChange={(e) => setMedName(e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Dose"
                  value={medDose}
                  onChange={(e) => setMedDose(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Stack>
            )}
            <TextField
              label="Body"
              multiline
              minRows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !body.trim() || !occurredAt}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
